import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const godsAndGoddesses = [
  {
    id: 1,
    name: "Ganesha",
    weapon: "Trishula",
    vehicle: "Mouse",
    description: "Remover of obstacles and god of new beginnings"
  },
  {
    id: 2,
    name: "Shiva",
    weapon: "Trishula",
    vehicle: "Nandi (Bull)",
    description: "The destroyer and transformer in the Hindu trinity"
  },
  {
    id: 3,
    name: "Parvati",
    weapon: "Trishula",
    vehicle: "Nandi (Bull)",
    description: "Goddess of love, fertility, and devotion"
  },
  {
    id: 4,
    name: "Lakshmi",
    weapon: "Lotus",
    vehicle: "Owl",
    description: "Goddess of wealth, fortune, and prosperity"
  },
  {
    id: 5,
    name: "Saraswati",
    weapon: "Veena",
    vehicle: "Swan",
    description: "Goddess of knowledge, music, and arts"
  },
  {
    id: 6,
    name: "Hanuman",
    weapon: "Gada (Mace)",
    vehicle: "None",
    description: "Devotee of Rama and symbol of strength and devotion"
  },
  {
    id: 7,
    name: "Krishna",
    weapon: "Sudarshana Chakra",
    vehicle: "Garuda",
    description: "Eighth avatar of Vishnu and divine charioteer"
  },
  {
    id: 8,
    name: "Rama",
    weapon: "Bow and Arrow",
    vehicle: "Hanuman",
    description: "Seventh avatar of Vishnu and prince of Ayodhya"
  }
];

const attributes = ["weapon", "vehicle", "description"];

const GodsAndGoddesses = () => {
  const [gameState, setGameState] = useState("playing"); // playing, completed
  const [score, setScore] = useState(0);
  const [currentRound, setCurrentRound] = useState(1);
  const [maxRounds] = useState(5);
  const [currentGod, setCurrentGod] = useState(null);
  const [currentAttribute, setCurrentAttribute] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Initialize game
  useEffect(() => {
    startNewRound();
  }, [currentRound]);

  const startNewRound = () => {
    if (currentRound > maxRounds) {
      setGameState("completed");
      return;
    }

    // Select a random god
    const randomGod = godsAndGoddesses[Math.floor(Math.random() * godsAndGoddesses.length)];
    
    // Select a random attribute
    const randomAttribute = attributes[Math.floor(Math.random() * attributes.length)];
    
    // Generate options
    let allOptions = [];
    if (randomAttribute === "weapon") {
      // Get unique weapons
      const uniqueWeapons = [...new Set(godsAndGoddesses.map(god => god.weapon))];
      allOptions = shuffleArray(uniqueWeapons);
    } else if (randomAttribute === "vehicle") {
      // Get unique vehicles
      const uniqueVehicles = [...new Set(godsAndGoddesses.map(god => god.vehicle))];
      allOptions = shuffleArray(uniqueVehicles);
    } else {
      // Get unique descriptions (first few words)
      const uniqueDescriptions = godsAndGoddesses.map(god => god.description.split(" ").slice(0, 3).join(" ") + "...");
      allOptions = shuffleArray(uniqueDescriptions);
    }
    
    // Take first 4 options
    const roundOptions = allOptions.slice(0, 4);
    
    // Ensure correct answer is in options
    if (!roundOptions.includes(randomGod[randomAttribute])) {
      roundOptions[0] = randomGod[randomAttribute];
    }
    
    // Shuffle options again
    const shuffledOptions = shuffleArray(roundOptions);
    
    setCurrentGod(randomGod);
    setCurrentAttribute(randomAttribute);
    setOptions(shuffledOptions);
    setSelectedOption(null);
    setFeedback(null);
  };

  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const handleOptionSelect = (option) => {
    if (selectedOption) return; // Prevent multiple selections
    
    setSelectedOption(option);
    
    const isCorrect = option === currentGod[currentAttribute];
    
    if (isCorrect) {
      setScore(score + 1);
      setFeedback({ correct: true, message: "Correct! 🎉" });
    } else {
      setFeedback({ 
        correct: false, 
        message: `Incorrect! ${currentGod.name}'s ${currentAttribute} is ${currentGod[currentAttribute]}`,
        correctAnswer: currentGod[currentAttribute]
      });
    }
    
    // Move to next round after delay
    setTimeout(() => {
      if (currentRound < maxRounds) {
        setCurrentRound(currentRound + 1);
      } else {
        setGameState("completed");
      }
    }, 2000);
  };

  const restartGame = () => {
    setGameState("playing");
    setScore(0);
    setCurrentRound(1);
    setCurrentGod(null);
    setCurrentAttribute(null);
    setOptions([]);
    setSelectedOption(null);
    setFeedback(null);
  };

  const getAttributeLabel = (attribute) => {
    switch (attribute) {
      case "weapon": return "weapon";
      case "vehicle": return "vehicle (vahana)";
      case "description": return "role";
      default: return attribute;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {gameState === "playing" && currentGod && (
        <div className="bg-gradient-to-br from-red-50 to-yellow-50 rounded-2xl p-6 md:p-8 shadow-lg">
          <div className="flex flex-wrap justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-red-600">Gods & Goddesses</h2>
            <div className="flex gap-4">
              <div className="bg-yellow-100 px-4 py-2 rounded-full">
                <span className="font-bold text-red-600">Score: {score}</span>
              </div>
              <div className="bg-red-100 px-4 py-2 rounded-full">
                <span className="font-bold text-red-600">Round: {currentRound}/{maxRounds}</span>
              </div>
            </div>
          </div>
          
          <div className="mb-2">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <motion.div 
                className="bg-red-600 h-4 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(currentRound / maxRounds) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 mb-6 shadow-md text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              What is {currentGod.name}'s {getAttributeLabel(currentAttribute)}?
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
              {options.map((option, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-4 rounded-xl border-2 text-left transition-all duration-300 ${
                    selectedOption === null 
                      ? "bg-white hover:bg-yellow-50 border-yellow-300" 
                      : selectedOption === option
                        ? option === currentGod[currentAttribute]
                          ? "bg-green-100 border-green-500"
                          : "bg-red-100 border-red-500"
                        : option === currentGod[currentAttribute]
                          ? "bg-green-100 border-green-500"
                          : "bg-gray-50 border-gray-300"
                  }`}
                  onClick={() => !selectedOption && handleOptionSelect(option)}
                  disabled={selectedOption !== null}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-3">
                      <span className="font-bold text-red-600">{index + 1}</span>
                    </div>
                    <span className="font-medium">{option}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
          
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-xl p-6 mb-6 shadow-md text-center ${
                feedback.correct ? "bg-green-100 border border-green-300" : "bg-red-100 border border-red-300"
              }`}
            >
              <h3 className="text-xl font-semibold mb-2">
                {feedback.message}
              </h3>
              {!feedback.correct && (
                <p className="text-gray-700">
                  {currentGod.name} is the {currentGod.description.toLowerCase()}.
                </p>
              )}
            </motion.div>
          )}
        </div>
      )}
      
      {gameState === "completed" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-red-50 to-yellow-100 rounded-2xl p-8 text-center shadow-lg"
        >
          <h2 className="text-3xl font-bold text-red-600 mb-4">Game Completed!</h2>
          <div className="text-5xl font-bold mb-4 text-yellow-500">
            {score}/{maxRounds}
          </div>
          <p className="text-xl text-gray-700 mb-2">
            {score === maxRounds 
              ? "Perfect! You're a mythology expert! 🏆" 
              : score >= maxRounds / 2 
                ? "Great job! You know your gods and goddesses well! 👍" 
                : "Keep learning! You'll get better! 💪"}
          </p>
          <p className="text-gray-600 mb-8">
            You scored {Math.round((score / maxRounds) * 100)}%
          </p>
          
          <button
            onClick={restartGame}
            className="bg-red-600 text-white font-bold py-3 px-8 rounded-full hover:bg-red-700 transition-colors shadow-lg"
          >
            Play Again
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default GodsAndGoddesses;